/**
 * @fileoverview Beneficiaries Management Page - Integrated Design
 * Features: Active beneficiaries list, invite system with QR, role assignment
 */
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'react-hot-toast';
import clsx from 'clsx';

export const BeneficiariesPage = () => {
    const { token, user } = useAuth();
    const { t } = useLanguage();

    const [activeTab, setActiveTab] = useState('active');
    const [students, setStudents] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Invitation state
    const [inviteForm, setInviteForm] = useState({
        email: '',
        firstName: '',
        lastName: ''
    });
    const [generatedInvite, setGeneratedInvite] = useState(null);
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    const fetchData = async () => {
        setLoading(true);
        try {
            if (activeTab === 'active') {
                await fetchStudents();
            } else if (activeTab === 'assign') {
                await fetchAllUsers();
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchStudents = async () => {
        try {
            const response = await fetch('/api/users/students', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setStudents(data.students);
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error(t('beneficiaries.toast.error_load_students', 'Error al cargar estudiantes'));
        }
    };

    const fetchAllUsers = async () => {
        try {
            const response = await fetch('/api/users', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setAllUsers(data.users || []);
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error(t('beneficiaries.toast.error_load_users', 'Error al cargar usuarios'));
        }
    };

    const handleCreateInvitation = async (e) => {
        e.preventDefault();

        try {
            const response = await fetch('/api/invitations/create', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(inviteForm)
            });

            if (response.ok) {
                const data = await response.json();
                setGeneratedInvite(data);

                // Generate QR Code (using dynamic import for code splitting)
                const QRCode = (await import('qrcode')).default;
                const inviteUrl = `${window.location.origin}/register?invite=${data.inviteToken}`;
                const qr = await QRCode.toDataURL(inviteUrl, {
                    width: 280,
                    margin: 2,
                    color: { dark: '#192A42', light: '#FFFFFF' }
                });
                setQrCodeUrl(qr);

                toast.success(t('beneficiaries.toast.invite_created', '¡Invitación creada exitosamente!'));
            } else {
                throw new Error('Error al crear invitación');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error(error.message);
        }
    };

    const handleSendEmail = async () => {
        if (!generatedInvite || sendingEmail) return;

        setSendingEmail(true);
        try {
            const response = await fetch('/api/invitations/send-email', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    inviteToken: generatedInvite.inviteToken,
                    email: inviteForm.email,
                    firstName: inviteForm.firstName
                })
            });

            if (response.ok) {
                toast.success(`📧 ${t('beneficiaries.toast.invite_sent', 'Invitación enviada a {email}').replace('{email}', inviteForm.email)}`);
                resetInviteForm();
            } else {
                throw new Error('Error al enviar email');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error(error.message);
        } finally {
            setSendingEmail(false);
        }
    };

    const handleAssignRole = async (userId, currentRole) => {
        const newRole = currentRole === 'student' ? null : 'student';

        try {
            const response = await fetch(`/api/users/${userId}/role`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ role: newRole })
            });

            if (response.ok) {
                toast.success(newRole === 'student'
                    ? `✅ ${t('beneficiaries.toast.role_assigned', 'Rol de estudiante asignado')}`
                    : `❌ ${t('beneficiaries.toast.role_removed', 'Rol de estudiante removido')}`
                );
                fetchAllUsers();
            } else {
                throw new Error('Error al asignar rol');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error(error.message);
        }
    };

    const resetInviteForm = () => {
        setInviteForm({ email: '', firstName: '', lastName: '' });
        setGeneratedInvite(null);
        setQrCodeUrl('');
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success(`📋 ${t('beneficiaries.toast.copied', 'Copiado al portapapeles')}`);
    };

    const filteredStudents = students.filter(s =>
        s.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.walletAddress && s.walletAddress.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const filteredUsers = allUsers.filter(u =>
        u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.lastName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const studentsWithWallet = filteredStudents.filter(s => s.hasWallet);
    const studentsWithoutWallet = filteredStudents.filter(s => !s.hasWallet);

    return (
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
            {/* Header - Consistent Style */}
            <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-[#192A42] tracking-tight">
                        {t('beneficiaries.page_title', 'Gestión de Beneficiarios')}
                    </h1>
                    <p className="mt-2 text-slate-500 text-lg">
                        {t('beneficiaries.page_subtitle', 'Administra estudiantes, invita nuevos beneficiarios y asigna roles')}
                    </p>
                </div>
            </div>

            {/* Tabs - Integrated Style */}
            <div className="flex items-center gap-3 mb-6">
                {[
                    { id: 'active', label: t('beneficiaries.tabs.active', 'Beneficiarios Activos'), icon: 'users' },
                    { id: 'invite', label: t('beneficiaries.tabs.invite', 'Invitar Nuevo'), icon: 'user-plus' },
                    { id: 'assign', label: t('beneficiaries.tabs.assign', 'Asignar Roles'), icon: 'user-tag' }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={clsx(
                            "px-4 py-3.5 rounded-lg font-medium transition-all flex items-center gap-2",
                            activeTab === tab.id
                                ? "bg-[#2563EB] text-white"
                                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        )}
                    >
                        <i className={`fas fa-${tab.icon}`}></i>
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Search Bar */}
            {(activeTab === 'active' || activeTab === 'assign') && (
                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 mb-6">
                    <div className="relative">
                        <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400"></i>
                        <input
                            type="text"
                            placeholder={t('beneficiaries.search.placeholder', 'Buscar por nombre, email o wallet...')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 pl-11 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2563EB] text-[#192A42]"
                        />
                    </div>
                </div>
            )}

            {/* Tab Content */}
            {activeTab === 'active' && (
                <ActiveBeneficiariesTab
                    studentsWithWallet={studentsWithWallet}
                    studentsWithoutWallet={studentsWithoutWallet}
                    loading={loading}
                    t={t}
                />
            )}

            {activeTab === 'invite' && (
                <InviteTab
                    inviteForm={inviteForm}
                    setInviteForm={setInviteForm}
                    handleCreateInvitation={handleCreateInvitation}
                    generatedInvite={generatedInvite}
                    qrCodeUrl={qrCodeUrl}
                    handleSendEmail={handleSendEmail}
                    sendingEmail={sendingEmail}
                    copyToClipboard={copyToClipboard}
                    resetInviteForm={resetInviteForm}
                    t={t}
                />
            )}

            {activeTab === 'assign' && (
                <AssignRolesTab
                    users={filteredUsers}
                    handleAssignRole={handleAssignRole}
                    loading={loading}
                    t={t}
                />
            )}
        </div>
    );
};

// Active Beneficiaries Tab
const ActiveBeneficiariesTab = ({ studentsWithWallet, studentsWithoutWallet, loading, t }) => {
    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <div className="w-12 h-12 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-slate-500 font-medium">{t('beneficiaries.loading', 'Cargando beneficiarios...')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border-2 border-green-200">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-green-700">{t('beneficiaries.stats.with_wallet', 'Con Wallet Activo')}</span>
                        <i className="fas fa-check-circle text-2xl text-green-600"></i>
                    </div>
                    <div className="text-4xl font-extrabold text-green-900">{studentsWithWallet.length}</div>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl border-2 border-orange-200">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-orange-700">{t('beneficiaries.stats.pending_wallet', 'Pendiente de Wallet')}</span>
                        <i className="fas fa-clock text-2xl text-orange-600"></i>
                    </div>
                    <div className="text-4xl font-extrabold text-orange-900">{studentsWithoutWallet.length}</div>
                </div>
            </div>

            {/* Students with Wallet */}
            {studentsWithWallet.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
                        <h3 className="font-bold text-[#192A42] flex items-center gap-2">
                            <i className="fas fa-check-circle text-green-500"></i>
                            {t('beneficiaries.list.ready_title', 'Beneficiarios Listos')} ({studentsWithWallet.length})
                        </h3>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {studentsWithWallet.map(student => (
                            <div key={student.id} className="px-6 py-4 hover:bg-slate-50 transition-colors flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                                    {student.firstName?.[0] || student.username?.[0] || '?'}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-[#192A42] truncate">{student.fullName}</div>
                                    <div className="text-sm text-slate-500 truncate">{student.email}</div>
                                    {student.walletAddress && (
                                        <code className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-600 mt-1 inline-block font-mono">
                                            <i className="fas fa-wallet mr-1"></i>
                                            {student.walletAddress.substring(0, 10)}...{student.walletAddress.substring(student.walletAddress.length - 8)}
                                        </code>
                                    )}
                                </div>
                                <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-bold whitespace-nowrap">
                                    <i className="fas fa-check-circle mr-1"></i>
                                    {t('beneficiaries.status.active', 'Activo')}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Students without Wallet */}
            {studentsWithoutWallet.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="bg-orange-50 px-6 py-4 border-b border-orange-200">
                        <h3 className="font-bold text-orange-900 flex items-center gap-2">
                            <i className="fas fa-exclamation-triangle text-orange-500"></i>
                            {t('beneficiaries.list.pending_title', 'Pendientes de Configuración')} ({studentsWithoutWallet.length})
                        </h3>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {studentsWithoutWallet.map(student => (
                            <div key={student.id} className="px-6 py-4 hover:bg-orange-50/30 transition-colors flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                                    {student.firstName?.[0] || student.username?.[0] || '?'}
                                </div>
                                <div className="flex-1">
                                    <div className="font-bold text-[#192A42]">{student.fullName}</div>
                                    <div className="text-sm text-slate-500">{student.email}</div>
                                </div>
                                <span className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg text-sm font-bold whitespace-nowrap">
                                    <i className="fas fa-wallet mr-1"></i>
                                    {t('beneficiaries.status.no_wallet', 'Sin Wallet')}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="bg-orange-50 px-6 py-3 border-t border-orange-200">
                        <p className="text-sm text-orange-700">
                            <i className="fas fa-info-circle mr-2"></i>
                            {t('beneficiaries.list.pending_info', 'Estos estudiantes deben crear su wallet antes de recibir certificados')}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

// Invite New Tab
const InviteTab = ({ inviteForm, setInviteForm, handleCreateInvitation, generatedInvite, qrCodeUrl, handleSendEmail, sendingEmail, copyToClipboard, resetInviteForm, t }) => {
    if (!generatedInvite) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 max-w-2xl mx-auto">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-[#2563EB] rounded-xl flex items-center justify-center mx-auto mb-4">
                        <i className="fas fa-user-plus text-3xl text-white"></i>
                    </div>
                    <h2 className="text-2xl font-extrabold text-[#192A42] mb-2">{t('beneficiaries.invite.title', 'Invitar Nuevo Beneficiario')}</h2>
                    <p className="text-slate-500">{t('beneficiaries.invite.subtitle', 'Crea una invitación con link único y código QR')}</p>
                </div>

                <form onSubmit={handleCreateInvitation} className="space-y-6">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                            {t('beneficiaries.invite.first_name', 'Nombre')} <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            value={inviteForm.firstName}
                            onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                            className="w-full px-3 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                            placeholder="Juan"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                            {t('beneficiaries.invite.last_name', 'Apellido')} <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            value={inviteForm.lastName}
                            onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                            className="w-full px-3 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                            placeholder="Pérez"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                            Email <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="email"
                            required
                            value={inviteForm.email}
                            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                            className="w-full px-3 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                            placeholder="juan.perez@example.com"
                        />
                    </div>

                    <button
                        type="submit"
                        className="w-full px-6 py-3.5 rounded-lg bg-[#2563EB] text-white font-bold hover:bg-[#1d4ed8] transition-all shadow-md hover:shadow-lg"
                    >
                        <i className="fas fa-paper-plane mr-2"></i>
                        {t('beneficiaries.invite.generate_button', 'Generar Invitación')}
                    </button>
                </form>
            </div>
        );
    }

    const inviteUrl = `${window.location.origin}/register?invite=${generatedInvite.inviteToken}`;

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 max-w-2xl mx-auto">
            <div className="text-center mb-8">
                <div className="w-16 h-16 bg-green-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-check-circle text-3xl text-white"></i>
                </div>
                <h2 className="text-2xl font-extrabold text-[#192A42] mb-2">{t('beneficiaries.invite.created_title', '¡Invitación Creada!')}</h2>
                <p className="text-slate-500">{t('beneficiaries.invite.created_subtitle', 'Para: {name}').replace('{name}', `${inviteForm.firstName} ${inviteForm.lastName}`)}</p>
            </div>

            {/* QR Code */}
            <div className="text-center mb-8">
                <div className="inline-block bg-white p-4 rounded-xl border-2 border-slate-200 shadow-md">
                    <img src={qrCodeUrl} alt="QR Code" className="w-64 h-64" />
                </div>
                <p className="mt-3 text-sm text-slate-500">{t('beneficiaries.invite.qr_scan', 'Escanea para registrarte instantáneamente')}</p>
            </div>

            {/* Link */}
            <div className="mb-6">
                <label className="block text-sm font-bold text-slate-700 mb-2">{t('beneficiaries.invite.link_label', 'Link de Invitación')}</label>
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={inviteUrl}
                        readOnly
                        className="flex-1 px-3 py-3 border border-slate-200 rounded-lg bg-slate-50 font-mono text-sm"
                    />
                    <button
                        onClick={() => copyToClipboard(inviteUrl)}
                        className="px-4 py-3 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-lg transition-colors"
                    >
                        <i className="fas fa-copy"></i>
                    </button>
                </div>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-3">
                <button
                    onClick={handleSendEmail}
                    disabled={sendingEmail}
                    className="px-6 py-3.5 rounded-lg bg-[#2563EB] text-white font-bold hover:bg-[#1d4ed8] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {sendingEmail ? (
                        <>
                            <i className="fas fa-circle-notch fa-spin mr-2"></i>
                            {t('beneficiaries.invite.sending_email', 'Enviando...')}
                        </>
                    ) : (
                        <>
                            <i className="fas fa-envelope mr-2"></i>
                            {t('beneficiaries.invite.send_email', 'Enviar Email')}
                        </>
                    )}
                </button>
                <button
                    onClick={resetInviteForm}
                    className="px-6 py-3.5 rounded-lg bg-slate-100 text-slate-700 font-bold hover:bg-slate-200 transition-all"
                >
                    <i className="fas fa-redo mr-2"></i>
                    {t('beneficiaries.invite.new_invitation', 'Nueva Invitación')}
                </button>
            </div>
        </div>
    );
};

// Assign Roles Tab
const AssignRolesTab = ({ users, handleAssignRole, loading, t }) => {
    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <div className="w-12 h-12 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-slate-500 font-medium">{t('beneficiaries.assign.loading', 'Cargando usuarios...')}</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
                <h2 className="font-bold text-[#192A42]">
                    <i className="fas fa-user-tag mr-2"></i>
                    {t('beneficiaries.assign.title', 'Usuarios del Tenant')}
                </h2>
                <p className="text-sm text-slate-500 mt-1">{t('beneficiaries.assign.subtitle', 'Asigna o remueve el rol de estudiante')}</p>
            </div>

            <div className="divide-y divide-slate-100">
                {users.map(user => (
                    <div key={user.id} className="px-6 py-4 hover:bg-slate-50 transition-colors flex items-center gap-4">
                        <div className={clsx(
                            "w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0",
                            user.role === 'student'
                                ? "bg-gradient-to-br from-blue-500 to-purple-600"
                                : "bg-slate-300"
                        )}>
                            {user.firstName?.[0] || user.username?.[0] || '?'}
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-[#192A42]">
                                {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username}
                            </div>
                            <div className="text-sm text-slate-500 flex items-center gap-3">
                                <span>{user.email}</span>
                                {user.walletAddress && (
                                    <code className="bg-slate-100 px-2 py-0.5 rounded text-xs font-mono">
                                        <i className="fas fa-wallet mr-1"></i>
                                        {user.walletAddress.substring(0, 8)}...
                                    </code>
                                )}
                            </div>
                        </div>

                        {user.role === 'student' && (
                            <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-bold">
                                <i className="fas fa-graduation-cap mr-1"></i>
                                {t('beneficiaries.assign.role_student', 'Estudiante')}
                            </span>
                        )}

                        <button
                            onClick={() => handleAssignRole(user.id, user.role)}
                            className={clsx(
                                "px-4 py-2.5 rounded-lg font-bold transition-all whitespace-nowrap",
                                user.role === 'student'
                                    ? "bg-red-100 text-red-700 hover:bg-red-200"
                                    : "bg-green-100 text-green-700 hover:bg-green-200"
                            )}
                        >
                            {user.role === 'student' ? (
                                <><i className="fas fa-times-circle mr-2"></i>{t('beneficiaries.assign.button_remove', 'Remover Rol')}</>
                            ) : (
                                <><i className="fas fa-plus-circle mr-2"></i>{t('beneficiaries.assign.button_assign', 'Asignar Estudiante')}</>
                            )}
                        </button>
                    </div>
                ))}

                {users.length === 0 && (
                    <div className="text-center py-16 text-slate-500">
                        <i className="fas fa-users-slash text-4xl mb-3 text-slate-300"></i>
                        <p>{t('beneficiaries.assign.no_users', 'No se encontraron usuarios')}</p>
                    </div>
                )}
            </div>
        </div>
    );
};
